import { useCallback, useEffect } from 'react';
import { DataStore } from '~/store';
import { AppLoader } from '~/components/app-loader';
import {
  FundContactForm,
  FundFacts,
  PageContainer,
  useModal,
  CurrencyIcon,
  TextDisplay,
  TextBody,
  useDateFormat,
  NoDateFallback,
} from '@xbto/universal-components';
import { theme } from '@xbto/design-system';
import {
  BuildEnv,
  DATE_FORMATS,
  EnrichedCurrencyInformation,
  getRuntimeEnv,
  useEnvironmentConfig,
  useTrackEvent,
} from 'common';
import { saveBlobAsFile } from '~/utils/save-blob-as-file';
import { GenericError } from '~/components/generic-error';

export const AssetDetailExperienceFunds = ({
  currency,
}: {
  currency?: EnrichedCurrencyInformation;
}) => {
  const { REACT_APP_BUILD_ENV } = useEnvironmentConfig();
  const { isLowerRegionEnv } = getRuntimeEnv(REACT_APP_BUILD_ENV as BuildEnv);

  const accessToken = DataStore.useStoreState(s => s.user.accessToken);
  const latestFundFactsheet = DataStore.useStoreState(
    s => s.holding.latestFundFactsheet
  );
  const fundFacts = DataStore.useStoreState(s => s.holding.fundFacts);
  const isLoading = DataStore.useStoreState(s => s.holding.busy);

  const identity = DataStore.useStoreState(s => s.user.identity);
  const relationshipManager = DataStore.useStoreState(
    s => s.settings.relationshipManager
  );

  const getFundFactsheetDownloadUrl = DataStore.useStoreActions(
    a => a.holding.getFundFactsheetDownloadUrl
  );
  const getFundFacts = DataStore.useStoreActions(a => a.holding.getFundFacts);
  const getFunds = DataStore.useStoreActions(a => a.markets.getFunds);

  /**
   * Hooks
   */
  const trackEvent = useTrackEvent();
  const { Modal: InvestNowModal, open, close } = useModal();
  const accountId = DataStore.useStoreState(s => s.user.defaultAccountId) || '';
  const currencyCode = currency?.code || '';

  const asOfDate = useDateFormat(
    fundFacts?.data?.asOfDate,
    DATE_FORMATS.DD_MMM_YYYY,
    false
  );

  useEffect(() => {
    if (currencyCode) {
      getFundFacts({ asset: currencyCode, accountId });
    }
  }, [accountId, currencyCode, getFundFacts]);

  const handlePressDownloadLatest = useCallback(() => {
    trackEvent('download-factsheet_cta_clicked', {
      source: 'markets-funds-page',
    });

    if (accountId) {
      getFundFactsheetDownloadUrl({
        asset: currencyCode,
        accountId,
        year: null,
        month: null,
        downloadCallback: (blob: Blob, filename: string) => {
          saveBlobAsFile(blob, filename);
        },
      });
    }
  }, [
    accountId,
    currencyCode,
    getFundFactsheetDownloadUrl,
    latestFundFactsheet,
    trackEvent,
  ]);

  const handleBeforeSubmit = useCallback(() => {
    trackEvent('contact-form_send-cta_clicked', {
      source: 'invest_flow',
      currencyCode: currency?.code,
    });
  }, [currency?.code, trackEvent]);

  const onSubscribePressed = useCallback(async () => {
    if (identity) {
      const funds = await getFunds();
      trackEvent('fund-subscription_cta_clicked', {
        source: 'markets-funds-page',
      });
      open({
        Body: (
          <FundContactForm
            mode="invest"
            accessToken={accessToken}
            fundCode={currency?.code}
            funds={funds ?? []}
            identity={identity}
            manager={relationshipManager}
            onBeforeSubmit={handleBeforeSubmit}
            onClose={close}
          />
        ),
      });
    }
  }, [
    accessToken,
    close,
    currency?.code,
    getFunds,
    handleBeforeSubmit,
    identity,
    open,
    relationshipManager,
    trackEvent,
  ]);

  if (!currency || isLoading) {
    return (
      <div className="flex-1">
        <AppLoader isFixed spinnerTop="104px" />
      </div>
    );
  }

  return (
    <PageContainer>
      <div className="pt-6 mx-auto" style={{ flex: 1, maxWidth: 762 }}>
        {fundFacts.errored ? (
          <GenericError
            error={
              isLowerRegionEnv
                ? new Error(fundFacts.errorMessage || '')
                : undefined
            }
            containerCls="h-full"
          />
        ) : (
          <>
            <div className="flex flex-col flex-1">
              <CurrencyIcon code={currency.code} size="lg" />
              <TextDisplay
                small
                style={{
                  marginTop: theme.spacing['4'],
                  marginBottom: theme.spacing['2'],
                }}
              >
                {currency.name}
              </TextDisplay>
              {asOfDate !== NoDateFallback && (
                <TextBody>As of {asOfDate}</TextBody>
              )}
            </div>
            {!!fundFacts && (
              <div className="py-8">
                <FundFacts
                  isMarketAssetPage
                  data={fundFacts.data}
                  onPressDownloadFactSheet={handlePressDownloadLatest}
                  onPressSubscribe={onSubscribePressed}
                />
              </div>
            )}
          </>
        )}
      </div>
      <InvestNowModal />
    </PageContainer>
  );
};
